/**
 * @fileoverview Game Result Overlay Component
 * @description Modal overlay shown when game ends with win/lose/draw animations
 */

import React, { useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Animated,
  Dimensions,
} from "react-native";
import LottieView from "lottie-react-native";
import { PlayerSymbol, GameStatus } from "../types";

interface GameResultOverlayProps {
  visible: boolean;
  gameStatus: GameStatus;
  winner: PlayerSymbol | null;
  mySymbol: PlayerSymbol | null;
  opponentName: string;
  onExit: () => void;
  onClose: () => void;
  /** Optional series info for best-of games */
  seriesInfo?: {
    myScore: number;
    opponentScore: number;
    isSeriesWinner: boolean;
    isSeriesLoser: boolean;
    gamesToWin: number;
  };
}

const { width, height } = Dimensions.get("window");

/**
 * GameResultOverlay - Shown when the game ends
 */
export const GameResultOverlay: React.FC<GameResultOverlayProps> = ({
  visible,
  gameStatus,
  winner,
  mySymbol,
  opponentName,
  onExit,
  onClose,
  seriesInfo,
}) => {
  const scaleAnim = React.useRef(new Animated.Value(0)).current;
  const fadeAnim = React.useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 5,
          tension: 100,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      scaleAnim.setValue(0);
      fadeAnim.setValue(0);
    }
  }, [visible, scaleAnim, fadeAnim]);

  const isWinner = winner === mySymbol;
  const isDraw = gameStatus === "draw";

  /**
   * Get result configuration based on game outcome
   */
  const getResultConfig = () => {
    // Series result takes priority
    if (seriesInfo) {
      const scoreText = `${seriesInfo.myScore} - ${seriesInfo.opponentScore}`;
      
      if (seriesInfo.isSeriesWinner) {
        return {
          title: "Serie Gewonnen!",
          subtitle: `Je hebt de serie gewonnen met ${scoreText}!`,
          emoji: "🏆",
          color: "#22C55E",
          lottie: require("@/assets/lottie/confetti.json"),
        };
      }
      
      if (seriesInfo.isSeriesLoser) {
        return {
          title: "Serie Verloren",
          subtitle: `${opponentName} wint de serie met ${scoreText}`,
          emoji: "😔",
          color: "#EF4444",
          lottie: require("@/assets/lottie/Holding_Back_Tears.json"),
        };
      }
    }

    if (isDraw) {
      return {
        title: "Gelijkspel!",
        subtitle: "Niemand wint, niemand verliest",
        emoji: "🤝",
        color: "#F59E0B",
        lottie: require("@/assets/lottie/finger_crossed.json"),
      };
    }

    if (isWinner) {
      return {
        title: "Gewonnen!",
        subtitle: `Je hebt ${opponentName} verslagen!`,
        emoji: "🎉",
        color: "#22C55E",
        lottie: require("@/assets/lottie/confetti.json"),
      };
    }

    return {
      title: "Verloren",
      subtitle: `${opponentName} was deze keer beter`,
      emoji: "😔",
      color: "#EF4444",
      lottie: require("@/assets/lottie/Holding_Back_Tears.json"),
    };
  };

  const config = getResultConfig();

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <Animated.View style={[styles.backdrop, { opacity: fadeAnim }]}>
        <TouchableOpacity
          style={styles.backdropTouch}
          activeOpacity={1}
          onPress={onClose}
        />

        <Animated.View
          style={[
            styles.container,
            {
              transform: [{ scale: scaleAnim }],
              borderColor: config.color,
            },
          ]}
        >
          {/* Lottie Animation */}
          <View style={styles.lottieContainer}>
            <LottieView
              source={config.lottie}
              autoPlay
              loop={isWinner}
              style={styles.lottie}
            />
          </View>

          {/* Result Text */}
          <Text style={styles.emoji}>{config.emoji}</Text>
          <Text style={[styles.title, { color: config.color }]}>
            {config.title}
          </Text>
          <Text style={styles.subtitle}>{config.subtitle}</Text>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.exitButton]}
              onPress={onExit}
              accessibilityLabel="Terug naar overzicht"
              accessibilityRole="button"
            >
              <Text style={styles.exitButtonText}>Afsluiten</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  backdropTouch: {
    ...StyleSheet.absoluteFillObject,
  },
  container: {
    width: width * 0.85,
    maxWidth: 340,
    backgroundColor: "#1F2937",
    borderRadius: 24,
    padding: 24,
    alignItems: "center",
    borderWidth: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.4,
    shadowRadius: 16,
    elevation: 10,
  },
  lottieContainer: {
    width: 120,
    height: 120,
    marginBottom: 8,
  },
  lottie: {
    width: "100%",
    height: "100%",
  },
  emoji: {
    fontSize: 48,
    marginBottom: 8,
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: "#9CA3AF",
    textAlign: "center",
    marginBottom: 24,
  },
  buttonContainer: {
    width: "100%",
  },
  button: {
    width: "100%",
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
  },
  exitButton: {
    backgroundColor: "#8B5CF6",
  },
  exitButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default GameResultOverlay;

